import type { Assessment, CustomFormData } from '../types/form.types';
import BaseService from './BaseService';
import type { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { transformFormDataForAPI } from '@utils/apiTransform';
import { API_CONFIG } from '../config';

const ApiService = {
  fetchData<Response = unknown, Request = Record<string, unknown> | FormData | Array<unknown>>(
    param: AxiosRequestConfig<Request>
  ) {
    return new Promise<AxiosResponse<Response>>((resolve, reject) => {
      BaseService(param)
        .then((response: AxiosResponse<Response>) => {
          resolve(response);
        })
        .catch((errors: AxiosError) => {
          reject(errors);
        });
    });
  },

  async getLatestAssessment(): Promise<{ has_assessment: boolean; assessment?: Assessment; message?: string }> {
    const response = await this.fetchData<{ status: string; data: { has_assessment: boolean; assessment?: Assessment; message?: string } }>({
      url: API_CONFIG.endpoints.assessment.latest,
      method: 'GET',
    });
    return response.data.data;
  },

  async getAssessmentHistory(): Promise<{ has_assessments: boolean; assessments?: Assessment[]; message?: string; count?: number }> {
    const response = await this.fetchData<{ status: string; data: { has_assessments: boolean; assessments?: Assessment[]; message?: string; count?: number } }>({
      url: API_CONFIG.endpoints.assessment.history,
      method: 'GET',
    });
    return response.data.data;
  },

  async submitAssessment(
    data: CustomFormData,
    isGuest: boolean = false
  ): Promise<{
    risk_score: number;
    risk_level: string;
    risk_factors: string[];
    recommendations: string[];
    bmi: number;
  }> {
    const endpoint = isGuest ? API_CONFIG.endpoints.assessment.submitGuest : API_CONFIG.endpoints.assessment.submit;
    const transformedData = transformFormDataForAPI(data);

    const config: AxiosRequestConfig = {
      url: endpoint,
      method: 'POST',
      data: transformedData,
    };

    // BaseService interceptor will automatically add Authorization header if user is authenticated
    // For guest submissions, no token is needed

    const response = await this.fetchData<{ status: string; data: { risk_score: number; risk_level: string; risk_factors: string[]; recommendations: string[]; bmi: number } } | { risk_score: number; risk_level: string; risk_factors: string[]; recommendations: string[]; bmi: number }>(config);
    const responseData = response.data as { status?: string; data?: { risk_score: number; risk_level: string; risk_factors: string[]; recommendations: string[]; bmi: number } } | { risk_score: number; risk_level: string; risk_factors: string[]; recommendations: string[]; bmi: number };
    return (responseData as { status: string; data: { risk_score: number; risk_level: string; risk_factors: string[]; recommendations: string[]; bmi: number } }).data || responseData as { risk_score: number; risk_level: string; risk_factors: string[]; recommendations: string[]; bmi: number };
  },

};

export const apiService = ApiService;
export default ApiService;
